import {
  FiHome,
  FiEdit3,
  FiBriefcase,
  FiCompass,
  FiHeart,
  FiFolder,
  FiFileText,
  FiMail,
  FiHelpCircle,
  FiKey,
  FiGift,
  FiClipboard,
  FiTrendingUp,
  FiTag,
  FiCreditCard,
  FiLayers,
  FiShare2,
  FiUserCheck,
  FiUserPlus,
  FiImage,
} from "react-icons/fi";

const createItem = (item) => ({
  ...item,
  activePrefixes: item.activePrefixes ?? [item.to],
});

const startsWithPrefix = (pathname, prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`);

export const PARTNER_PANEL_HOME = "/partner/workspace";
export const PARTNER_TOOLS_HOME = "/partner/tools";

// The Partner-only tools. The sidebar TOOLS group and the Overview "Partner Tools" tiles both read this list.
export const partnerTools = [
  {
    to: "/partner/tools/cashbook",
    label: "Cashbook",
    description: "Record daily cash in and cash out and see your running balance.",
    icon: FiCreditCard,
  },
  {
    to: "/partner/tools/projects",
    label: "Project Ledger",
    description: "Keep advances, payments and notes for each project in one place.",
    icon: FiLayers,
  },
];

// "Partner Network" is a sidebar group, not a page: its crumb has no link
const PARTNER_NETWORK_PARENT = { label: "Partner Network" };

const partnerSidebarMenu = [
  {
    id: "dashboard",
    title: "Dashboard",
    items: [
      {
        to: "/partner/workspace",
        label: "Overview",
        icon: FiHome,
        activePrefixes: ["/partner/workspace", "/partner/dashboard", "/partner/myspace"],
      },
    ],
  },
  {
    id: "shared",
    title: "Shared",
    items: [
      {
        to: "/partner/posts",
        label: "Shared Posts",
        icon: FiEdit3,
      },
      {
        to: "/partner/properties",
        label: "Shared Properties",
        icon: FiBriefcase,
      },
      {
        to: "/partner/shared-leads",
        label: "Shared Leads",
        icon: FiCompass,
      },
      {
        to: "/partner/shared-leases",
        label: "Shared Leases",
        icon: FiKey,
      },
      {
        to: "/partner/promotions",
        label: "Shared Promotions",
        icon: FiTrendingUp,
      },
    ],
  },
  {
    id: "myspace",
    title: "My Space",
    items: [
      {
        to: "/partner/my-properties",
        label: "My Properties",
        icon: FiFolder,
      },
      {
        to: "/partner/my-posts",
        label: "My Posts",
        icon: FiFileText,
      },
      {
        to: "/partner/leads",
        label: "My Leads",
        icon: FiClipboard,
      },
      {
        to: "/partner/leases",
        label: "My Leases",
        icon: FiKey,
      },
      {
        to: "/partner/my-promotions",
        label: "My Promotions",
        icon: FiTag,
        formLabel: "Promotion",
      },
      {
        to: "/partner/rewards",
        label: "Rewards",
        icon: FiGift,
      },
      {
        to: "/partner/saved",
        label: "Following",
        icon: FiHeart,
        activePrefixes: ["/partner/saved", "/partner/following"],
      },
      {
        to: "/partner/shared-with-me",
        label: "Shared with Me",
        icon: FiMail,
      },
    ],
  },
  {
    id: "tools",
    title: "Tools",
    items: [
      ...partnerTools.map(({ to, label, icon }) => ({ to, label, icon })),
    ],
  },
  {
    id: "network",
    title: "Partner Network",
    items: [
      {
        to: "/partner/network/partners",
        label: "Partners",
        icon: FiShare2,
        formLabel: "Partner",
        createVerb: "Add",
        parent: PARTNER_NETWORK_PARENT,
      },
      {
        to: "/partner/network/clients",
        label: "Clients",
        icon: FiUserCheck,
        formLabel: "Client",
        createVerb: "Add",
        parent: PARTNER_NETWORK_PARENT,
      },
    ],
  },
  {
    id: "team",
    title: "Partner Team",
    items: [
      {
        to: "/partner/team",
        label: "Teams",
        crumbLabel: "Partner Team",
        icon: FiUserPlus,
        formLabel: "Team",
      },
    ],
  },
].map((section) => ({
  ...section,
  items: section.items.map(createItem),
}));

export const partnerSidebarSections = partnerSidebarMenu;

/* -------------------------------------------------------------------------------------------
 * Interior Design partner: catalog-only experience. All service-type menu config lives here.
 * ----------------------------------------------------------------------------------------- */
export const INTERIOR_CATALOG_PATH = "/partner/interior-design";

// Property-related pages hidden from (and unreachable for) Interior Design partners.
export const INTERIOR_HIDDEN_PREFIXES = [
  "/partner/my-properties",
  "/partner/properties",
  "/partner/leads",
  "/partner/shared-leads",
  "/partner/leases",
  "/partner/shared-leases",
];

export const isHiddenForInterior = (pathname) =>
  INTERIOR_HIDDEN_PREFIXES.some((prefix) => startsWithPrefix(pathname, prefix));

const INTERIOR_CATALOG_SECTION = {
  id: "catalog",
  title: "Catalog",
  items: [
    createItem({
      to: INTERIOR_CATALOG_PATH,
      label: "Catalog",
      icon: FiImage,
    }),
  ],
};

// Standard menu minus the property items, with the Catalog section right after the Overview.
const interiorSidebarMenu = [
  partnerSidebarMenu[0],
  INTERIOR_CATALOG_SECTION,
  ...partnerSidebarMenu.slice(1),
]
  .map((section) => ({
    ...section,
    items: section.items.filter((item) => !isHiddenForInterior(item.to)),
  }))
  .filter((section) => section.items.length > 0);

export const partnerSidebarFooter = [
  createItem({
    to: "/partner/help",
    label: "Help Center",
    icon: FiHelpCircle,
  }),
];

// Menu per normalised role. Unknown/loading role -> empty (no flash of a wrong menu, and the
// TOOLS group can never reach another role).
const SIDEBAR_BY_ROLE = {
  PARTNER: partnerSidebarMenu,
};

export const normalizeRole = (role) =>
  typeof role === "string" ? role.trim().toUpperCase() : "";

// `kind` is the resolved service type ("interior" | "standard" | "unknown"). Unknown -> no menu at all.
export const getPartnerSidebar = (role, kind = "standard") => {
  if (kind === "unknown") return [];
  const menu = SIDEBAR_BY_ROLE[normalizeRole(role)] ?? [];
  return kind === "interior" && menu.length ? interiorSidebarMenu : menu;
};

// Pages that are reachable but are not sidebar items. `parent` adds a middle breadcrumb.
const EXTRA_PAGES = [
  { prefix: PARTNER_TOOLS_HOME, label: "Tools" },
  { prefix: "/partner/interior-design", label: "Catalog" },
  { prefix: "/partner/local-bazaar", label: "Local Bazaar Store" },
  { prefix: "/partner/services", label: "My Service Offerings" },
  { prefix: "/partner/subscription", label: "Current Plan" },
  { prefix: "/partner/reminders", label: "Meeting Reminders" },
  { prefix: "/partner/communities", label: "Communities" },
  { prefix: "/partner/invitations", label: "Invitations" },
  { prefix: "/partner/teams", label: "Partner Teams" },
  // The Partner Network hub; the Partners / Clients pages live below it and win on the longer prefix
  { prefix: "/partner/network", label: "Partner Network" },
  { prefix: "/partner/profile", label: "Profile" },
  { prefix: "/partner/help", label: "Help Center" },
  { prefix: "/notification", label: "Notifications" },
  { prefix: "/add-property", label: "Create Property" },
  { prefix: "/property/add-property", label: "Create Property" },
  { prefix: "/post/add-post", label: "Create Post" },
  { prefix: "/add-promotion", label: "Create Promotion" },
];

const TOOLS_PARENT = { label: "Tools", to: PARTNER_TOOLS_HOME };

// Longest matching known page for a pathname (sidebar items first, then the extra pages)
const findPage = (pathname) => {
  const candidates = [
    ...partnerSidebarSections.flatMap((s) => s.items).flatMap((item) =>
      item.activePrefixes.map((prefix) => ({
        prefix,
        label: item.label,
        crumbLabel: item.crumbLabel,
        to: item.to,
        formLabel: item.formLabel,
        createVerb: item.createVerb,
        parent: item.parent ?? (startsWithPrefix(item.to, PARTNER_TOOLS_HOME) ? TOOLS_PARENT : undefined),
      }))
    ),
    ...EXTRA_PAGES.map((page) => ({ ...page, to: page.prefix })),
  ].filter((c) => startsWithPrefix(pathname, c.prefix));
  return candidates.sort((a, b) => b.prefix.length - a.prefix.length)[0];
};

export const isPartnerRoot = (pathname) =>
  pathname === "/partner" || pathname === "/partner/" || pathname === PARTNER_PANEL_HOME;

// Title Case of the last path segment, for a route that is not in the config yet (never a generic "Page")
const humanize = (pathname) =>
  (pathname.split("/").filter(Boolean).pop() ?? "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const getPartnerPageTitle = (pathname) =>
  findPage(pathname)?.label ?? (humanize(pathname) || "Overview");

/**
 * Breadcrumb trail for an inner page: Overview > <section> > <detail>
 * (tool pages: Overview > Tools > <Tool>). The last item has no `to` (current page).
 */
export const getPartnerBreadcrumbs = (pathname) => {
  if (isPartnerRoot(pathname)) return [];
  const crumbs = [{ label: "Overview", to: PARTNER_PANEL_HOME }];
  const page = findPage(pathname);
  if (!page) return [...crumbs, { label: humanize(pathname) || "Overview" }];

  if (page.parent) crumbs.push(page.parent);

  const rest = pathname.slice(page.prefix.length).split("/").filter(Boolean);
  const crumbLabel = page.crumbLabel ?? page.label;
  if (rest.length === 0) {
    crumbs.push({ label: crumbLabel });
  } else {
    crumbs.push({ label: crumbLabel, to: page.to });
    const last = rest[rest.length - 1];
    crumbs.push({
      label:
        rest.includes("members") && rest.includes("new")
          ? "Add Member"
          : rest.includes("new")
          ? [page.createVerb ?? "Create", page.formLabel].filter(Boolean).join(" ")
          : last === "edit"
            ? ["Edit", page.formLabel].filter(Boolean).join(" ")
            : last === "discussion"
              ? "Discussion"
              : "Details",
    });
  }
  return crumbs;
};

const partnerNavigation = {
  sections: partnerSidebarSections,
};

export default partnerNavigation;
